import { Palette } from "lucide-react";
import { Field, Surface } from "@/components/ds";
import { Input } from "@/components/ui/input";

export const THEME_PRESETS = [
  // chave, nome, principal, destaque, botão, fundo, cartão, áreas secundárias, texto
  ["tr3nd", "Azul escuro TR3ND", "#00b7e0", "#ff6a00", "#ff6a00",
   "#0b111c", "#141c2a", "#1b2434", "#f1f5f9"],
  ["clean", "Branco clean", "#2563eb", "#0ea5e9", "#111827",
   "#ffffff", "#f8fafc", "#f1f5f9", "#0f172a"],
  ["rosewhite", "Branco com rosa", "#e75480", "#f6b8c8", "#e75480",
   "#ffffff", "#fff5f8", "#fdeef3", "#3b1420"],
  ["rose", "Rosa suave", "#b84a6a", "#f6b8c8", "#d1577c",
   "#fdeef3", "#ffffff", "#fbe0e9", "#3b1420"],
  ["mint", "Verde menta", "#0f766e", "#5eead4", "#0f766e",
   "#f2fbf8", "#ffffff", "#e4f5ef", "#0d2b26"],
  ["midnight", "Violeta noturno", "#a78bfa", "#c3b3ff", "#7c5cff",
   "#11111b", "#1a1a27", "#232334", "#ece9ff"],
];

export const COLOR_KEYS = ["primary_color", "accent_color", "button_color",
                           "background_color", "card_color", "surface_color", "text_color"];

export const COLOR_LABELS = {
  primary_color: "principal", accent_color: "de destaque", button_color: "dos botões",
  background_color: "de fundo", card_color: "dos cartões",
  surface_color: "das áreas secundárias", text_color: "dos textos",
};

export const EMPTY_THEME = {
  theme_preset: "", primary_color: "", accent_color: "", button_color: "",
  background_color: "", card_color: "", surface_color: "", text_color: "",
};

export const HEX = /^#[0-9a-fA-F]{6}$/;

/** Luminância simples: decide se o texto sobre a cor deve ser escuro ou claro. */
export const isLight = (hex) => {
  if (!HEX.test(hex)) return false;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62;
};

export const themePreview = (form) => {
  const pick = (key, fallback) => (HEX.test(form[key]) ? form[key] : fallback);
  return {
    primary: pick("primary_color", "#00b7e0"),
    accent: pick("accent_color", "#ff6a00"),
    button: pick("button_color", "#ff6a00"),
    background: pick("background_color", "#0b111c"),
    card: pick("card_color", "#141c2a"),
    surface: pick("surface_color", "#1b2434"),
    text: pick("text_color", "#f1f5f9"),
  };
};

/** Valida os 7 hexadecimais. Devolve a mensagem do primeiro inválido ou "". */
export const themeColorError = (form) => {
  for (const key of COLOR_KEYS) {
    if (form[key] && !HEX.test(form[key]))
      return `A cor ${COLOR_LABELS[key]} precisa ser um hexadecimal como #1ABC9C.`;
  }
  return "";
};

export const themeColorPayload = (form) => ({
  theme_preset: form.theme_preset || "",
  ...Object.fromEntries(COLOR_KEYS.map((k) => [k, form[k] || null])),
});

export const ColorField = ({ label, value, onChange, testid }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={HEX.test(value) ? value : "#00b7e0"}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testid}-picker`}
        className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-secondary/40 p-1"
      />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
             placeholder="#00B7E0" data-testid={testid} className="font-num" />
      {value && (
        <button type="button" onClick={() => onChange("")}
                data-testid={`${testid}-clear`}
                className="shrink-0 text-[11px] text-muted-foreground hover:text-foreground">
          limpar
        </button>
      )}
    </div>
  </Field>
);

export const ThemePresets = ({ value, onApply, testidPrefix }) => (
  <Surface className="p-5">
    <p className="flex items-center gap-2 font-display text-base">
      <Palette className="h-4 w-4 text-brand" /> Temas prontos
    </p>
    <div className="mt-3 flex flex-wrap gap-2">
      {THEME_PRESETS.map((preset) => (
        <button
          key={preset[0]}
          type="button"
          onClick={() => onApply(preset)}
          data-testid={`${testidPrefix}-preset-${preset[0]}`}
          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
            value === preset[0]
              ? "border-brand bg-brand/10 text-brand"
              : "border-white/15 text-muted-foreground hover:text-foreground"
          }`}
        >
          <span className="flex gap-0.5">
            <span className="h-3 w-3 rounded-full border border-white/20"
                  style={{ background: preset[5] }} />
            <span className="h-3 w-3 rounded-full" style={{ background: preset[2] }} />
            <span className="h-3 w-3 rounded-full" style={{ background: preset[4] }} />
          </span>
          {preset[1]}
        </button>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">
      Escolher um tema preenche todas as cores abaixo. Depois é só ajustar o que quiser.
    </p>
  </Surface>
);

export const ThemeColorFields = ({ form, onChange, testidPrefix }) => (
  <div className="grid gap-3 md:grid-cols-3">
    {[["primary_color", "Cor principal", "primary"],
      ["accent_color", "Cor de destaque", "accent"],
      ["button_color", "Cor dos botões", "button"],
      ["background_color", "Fundo principal", "background"],
      ["card_color", "Fundo dos cartões", "card"],
      ["surface_color", "Áreas secundárias", "surface"],
      ["text_color", "Cor dos textos", "text"]].map(([key, label, slug]) => (
        <ColorField key={key} label={label} value={form[key]}
                    onChange={(v) => onChange(key, v)}
                    testid={`${testidPrefix}-color-${slug}`} />
      ))}
  </div>
);

/** Prévia representativa do catálogo: cabeçalho, busca, cartões, preços e botões. */
export const ThemePreviewCard = ({ form, title, subtitle, testid }) => {
  const c = themePreview(form);
  const buttonText = isLight(c.button) ? "#111827" : "#ffffff";
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10" data-testid={testid}>
      <div style={{ background: c.background, color: c.text }}>
        <div className="flex items-center justify-between gap-3 px-4 py-3"
             style={{ background: c.surface }}>
          <span className="font-display text-sm font-semibold" style={{ color: c.primary }}>
            {title || "TR3ND Studio"}
          </span>
          <span className="rounded-full px-3 py-1 text-[10px] font-semibold"
                style={{ background: c.button, color: buttonText }}>
            Carrinho · 2
          </span>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-[10px] uppercase tracking-wide" style={{ opacity: 0.6 }}>
            Prévia do catálogo
          </p>
          <p className="font-display text-lg" style={{ color: c.primary }}>
            {subtitle || "Peças que são a sua cara"}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex-1 rounded-full px-3 py-2 text-[11px]"
                  style={{ background: c.card, opacity: 0.85 }}>
              Buscar produtos...
            </span>
            <span className="rounded-full px-3 py-1.5 text-[10px] font-semibold"
                  style={{ background: c.button, color: buttonText }}>
              Todos
            </span>
            <span className="rounded-full px-3 py-1.5 text-[10px]"
                  style={{ background: c.card }}>
              Chaveiros
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[["Vaso geométrico", "R$ 49,90", "Pronta entrega"],
              ["Chaveiro personalizado", "R$ 24,90", "Sob encomenda"]].map(([n, price, tag]) => (
                <div key={n} className="rounded-xl p-3" style={{ background: c.card }}>
                  <div className="h-14 rounded-lg" style={{ background: c.surface }} />
                  <p className="mt-2 text-xs font-medium">{n}</p>
                  <p className="font-num text-sm" style={{ color: c.primary }}>{price}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold"
                          style={{ background: c.button, color: buttonText }}>
                      Adicionar
                    </span>
                    <span className="text-[10px]" style={{ color: c.accent }}>{tag}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
